import React, { useCallback, useEffect, useState } from 'react';
import { Info, Loader2, Lock } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './PainelControleDbVervifyCounters.css';

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_MIN_DIGITS = 6;

async function api(path, options = {}) {
  const authToken = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}/api/config/db-vervify${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) throw new Error(result.message || 'Falha na comunicação com o servidor');
  return result;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function validationMessage(value, data) {
  if (!value) return '';
  const n = Number(value);
  if (!Number.isSafeInteger(n) || n <= 0) return 'Número fora do intervalo permitido.';
  if (data?.erpMax != null && n <= data.erpMax) return `Deve ser maior que ${data.erpMax} (último número do ERP).`;
  const c = data?.counter;
  if (c?.lockedAt && n < c.nextValue) return `O contador já está em uso e não pode ser reduzido (mínimo ${c.nextValue}).`;
  return '';
}

export default function PainelControleDbVervifyCounters({ toastSuccess, toastError, onSaved, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('/counters');
      setData(r);
      setValue(r.counter ? String(r.counter.nextValue) : '');
    } catch (e) {
      toastError?.(e.message);
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const counter = data?.counter || null;
  const minDigits = counter?.minDigits || DEFAULT_MIN_DIGITS;
  const error = validationMessage(value, data);
  const dirty = value !== (counter ? String(counter.nextValue) : '');

  const save = async () => {
    if (!value || error || !dirty || saving) return;
    setSaving(true);
    try {
      const r = await api('/counters', { method: 'PUT', body: JSON.stringify({ nextValue: value }) });
      setData(r);
      setValue(r.counter ? String(r.counter.nextValue) : '');
      toastSuccess?.('Contador salvo');
      onSaved?.();
    } catch (e) {
      toastError?.(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="pc-dbv-cnt-note">
        <Loader2 size={13} strokeWidth={2} className="pcs2-spin" aria-hidden /> Carregando contador…
      </p>
    );
  }

  if (data && !data.available) {
    return (
      <p className="pc-dbv-cnt-note">
        <Info size={13} strokeWidth={2} aria-hidden />
        Aplique as atualizações de estrutura pendentes em Migração de dados para habilitar o contador.
      </p>
    );
  }

  return (
    <div className="pc-dbv-cnt">
      <div className="pcs2-setting-row">
        <div className="pcs2-setting-copy">
          <span className="pcs2-setting-title">Próximo número da OS</span>
          <span className="pcs2-setting-desc">
            {data?.erpMax != null ? `Último número de OS no ERP: ${data.erpMax}. ` : ''}
            Use um número maior, com folga: o ERP pode continuar numerando OS criadas fora do painel.
          </span>
        </div>
        <div className="pcs2-setting-actions">
          <input
            type="text"
            inputMode="numeric"
            className={`customers-oauth-input pc-dbv-cnt-input${error ? ' is-invalid' : ''}`}
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 15))}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            placeholder={data?.erpMax != null ? String(data.erpMax + 1) : 'Número inicial'}
            aria-label="Próximo número da OS"
            aria-invalid={Boolean(error)}
            disabled={saving}
          />
          <button
            type="button"
            className="pc-dbv-btn is-primary"
            onClick={save}
            disabled={!value || Boolean(error) || !dirty || saving}
          >
            {saving ? <Loader2 size={14} strokeWidth={2} className="pcs2-spin" aria-hidden /> : null}
            Salvar contador
          </button>
        </div>
      </div>

      {error ? (
        <p className="pc-dbv-cnt-error" role="alert">{error}</p>
      ) : value ? (
        <p className="pc-dbv-cnt-preview">
          Exibição da próxima OS: <b>{value.padStart(minDigits, '0')}</b>
        </p>
      ) : null}

      <p className="pc-dbv-cnt-note">
        {counter?.lockedAt ? (
          <>
            <Lock size={13} strokeWidth={2} aria-hidden />
            Em uso desde {formatDateTime(counter.lockedAt)}. O número só pode ser aumentado.
          </>
        ) : counter ? (
          <>
            <Info size={13} strokeWidth={2} aria-hidden />
            Ainda não utilizado; pode ser alterado até a primeira OS nativa.
            {counter.updatedBy ? ` Última alteração por ${counter.updatedBy} em ${formatDateTime(counter.updatedAt)}.` : ''}
          </>
        ) : (
          <>
            <Info size={13} strokeWidth={2} aria-hidden />
            Contador não configurado. Ele é obrigatório para ativar a operação nativa.
          </>
        )}
      </p>
    </div>
  );
}
